// /src/pages/AccessControlPage.js
// Admin screen for Ask & Answer data access: which SUBJECT AREAS (table groups)
// each department and role may query. Backed by /api/access/*.
import React, { useEffect, useState, useCallback } from "react";

function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(`/api${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then(async (res) => {
    if (!res.ok) {
      const raw = await res.text();
      let msg = raw;
      try { msg = JSON.parse(raw).error || raw; } catch { /* keep raw */ }
      throw new Error(msg || `${res.status} ${res.statusText}`);
    }
    return res.status === 204 ? null : res.json();
  });
}

const ROLE_SUGGESTIONS = ["user", "manager"];

export default function AccessControlPage() {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [expanded, setExpanded] = useState(null); // area id whose tables are open
  const [newArea, setNewArea] = useState({ key: "", label: "" });
  const [newRole, setNewRole] = useState("");

  const load = useCallback(async () => {
    try {
      setErr("");
      setCfg(await api("/access/config"));
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setNote(m); setTimeout(() => setNote(""), 1800); };

  async function toggleDeptArea(dept, areaId) {
    const has = dept.areaIds.includes(areaId);
    const next = has ? dept.areaIds.filter((a) => a !== areaId) : [...dept.areaIds, areaId];
    setCfg((c) => ({ ...c, departments: c.departments.map((d) => d.id === dept.id ? { ...d, areaIds: next } : d) }));
    try {
      await api(`/access/departments/${dept.id}/areas`, { method: "PUT", body: { areaIds: next } });
      flash(`Saved ${dept.name}`);
    } catch (e) { setErr(e.message); load(); }
  }

  async function toggleRoleArea(role, areaId) {
    const row = (cfg.roles.find((r) => r.role === role)) || { role, areaIds: [] };
    const has = row.areaIds.includes(areaId);
    const next = has ? row.areaIds.filter((a) => a !== areaId) : [...row.areaIds, areaId];
    setCfg((c) => {
      const others = c.roles.filter((r) => r.role !== role);
      return { ...c, roles: [...others, { role, areaIds: next }] };
    });
    try {
      await api(`/access/roles/${encodeURIComponent(role)}/areas`, { method: "PUT", body: { areaIds: next } });
      flash(`Saved role ${role}`);
    } catch (e) { setErr(e.message); load(); }
  }

  async function toggleAreaTable(area, source, table) {
    const cur = { ...(area.tables || {}) };
    const list = cur[source] ? [...cur[source]] : [];
    const has = list.includes(table);
    cur[source] = has ? list.filter((t) => t !== table) : [...list, table];
    setCfg((c) => ({ ...c, areas: c.areas.map((a) => a.id === area.id ? { ...a, tables: cur } : a) }));
    try {
      await api(`/access/areas/${area.id}`, { method: "PUT", body: { tables: cur } });
      flash(`Saved ${area.label}`);
    } catch (e) { setErr(e.message); load(); }
  }

  async function createArea() {
    if (!newArea.key.trim() || !newArea.label.trim()) return;
    try {
      await api("/access/areas", { method: "POST", body: newArea });
      setNewArea({ key: "", label: "" }); load(); flash("Area created");
    } catch (e) { setErr(e.message); }
  }

  async function deleteArea(a) {
    if (!window.confirm(`Delete subject area "${a.label}"? It will be removed from every department/role.`)) return;
    try { await api(`/access/areas/${a.id}`, { method: "DELETE" }); load(); flash("Area deleted"); }
    catch (e) { setErr(e.message); }
  }

  if (err && !cfg) return <div style={{ padding: 24, color: "#b00" }}>Error: {err}</div>;
  if (!cfg) return <div style={{ padding: 24 }}>Loading…</div>;

  const areas = cfg.areas;
  const roleRows = Array.from(new Set([
    ...cfg.roles.map((r) => r.role),
    ...ROLE_SUGGESTIONS,
  ])).filter((r) => r !== "admin");
  const roleAreaIds = (role) => (cfg.roles.find((r) => r.role === role) || {}).areaIds || [];

  const th = { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #e5e7eb", fontSize: 13 };
  const td = { padding: "8px 10px", borderBottom: "1px solid #f0f0f0", fontSize: 14 };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <h1 style={{ marginBottom: 4 }}>Ask &amp; Answer — Data Access</h1>
      <p style={{ color: "#666", marginTop: 0 }}>
        A user can query a subject area if their <b>department</b> or <b>role</b> is granted it.
        Enforcement is{" "}
        <b style={{ color: cfg.scopeMode ? "#137333" : "#b06000" }}>
          {cfg.scopeMode ? "ON" : "OFF (AA_SCOPE_MODE not set)"}
        </b>. Unconfigured users are{" "}
        <b>{cfg.defaultOpen ? "all-access (default-open)" : "denied (default-deny)"}</b>. Admins bypass scoping.
      </p>
      {err && <div style={{ color: "#b00", marginBottom: 8 }}>Error: {err}</div>}
      {note && <div style={{ color: "#137333", marginBottom: 8 }}>{note}</div>}

      {/* Departments × areas */}
      <h2 style={{ marginTop: 20 }}>Departments</h2>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead><tr>
          <th style={th}>Department</th>
          {areas.map((a) => <th key={a.id} style={{ ...th, textAlign: "center" }}>{a.label}</th>)}
        </tr></thead>
        <tbody>
          {cfg.departments.map((d) => (
            <tr key={d.id}>
              <td style={td}>
                {d.name}
                {d.areaIds.length === 0 &&
                  <span style={{ color: "#b06000", fontSize: 12 }}> — unrestricted</span>}
              </td>
              {areas.map((a) => (
                <td key={a.id} style={{ ...td, textAlign: "center" }}>
                  <input type="checkbox" checked={d.areaIds.includes(a.id)}
                         onChange={() => toggleDeptArea(d, a.id)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Roles × areas */}
      <h2 style={{ marginTop: 24 }}>Roles <span style={{ fontWeight: 400, color: "#888", fontSize: 13 }}>(baseline, unioned with department; admin always all-access)</span></h2>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead><tr>
          <th style={th}>Role</th>
          {areas.map((a) => <th key={a.id} style={{ ...th, textAlign: "center" }}>{a.label}</th>)}
        </tr></thead>
        <tbody>
          {roleRows.map((role) => (
            <tr key={role}>
              <td style={td}>{role}</td>
              {areas.map((a) => (
                <td key={a.id} style={{ ...td, textAlign: "center" }}>
                  <input type="checkbox" checked={roleAreaIds(role).includes(a.id)}
                         onChange={() => toggleRoleArea(role, a.id)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 8 }}>
        <input placeholder="add role name" value={newRole} onChange={(e) => setNewRole(e.target.value)}
               style={{ padding: 6 }} />
        <button onClick={() => { if (newRole.trim()) { toggleRoleArea(newRole.trim(), -1); setNewRole(""); } }}
                style={{ marginLeft: 6 }}>Add role</button>
        <span style={{ color: "#888", fontSize: 12, marginLeft: 8 }}>(then tick its areas)</span>
      </div>

      {/* Subject areas + their tables */}
      <h2 style={{ marginTop: 28 }}>Subject areas &amp; tables</h2>
      {areas.map((a) => (
        <div key={a.id} style={{ border: "1px solid #eee", borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><b>{a.label}</b> <span style={{ color: "#999", fontSize: 12 }}>({a.key})</span></div>
            <div>
              <button onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
                {expanded === a.id ? "Hide tables" : "Edit tables"}
              </button>
              <button onClick={() => deleteArea(a)} style={{ marginLeft: 6, color: "#b00" }}>Delete</button>
            </div>
          </div>
          <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
            {Object.entries(a.tables || {}).map(([s, ts]) => `${s}: ${ts.join(", ")}`).join("  ·  ") || "(no tables)"}
          </div>
          {expanded === a.id && (
            <div style={{ marginTop: 10 }}>
              {Object.entries(cfg.catalog).map(([source, tables]) => (
                <div key={source} style={{ marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{source}</div>
                  {tables.map((t) => (
                    <label key={t} style={{ display: "inline-block", marginRight: 12, fontSize: 13 }}>
                      <input type="checkbox"
                             checked={(a.tables?.[source] || []).includes(t)}
                             onChange={() => toggleAreaTable(a, source, t)} /> {t}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Create area */}
      <div style={{ marginTop: 8 }}>
        <input placeholder="key (e.g. qad_shipping)" value={newArea.key}
               onChange={(e) => setNewArea({ ...newArea, key: e.target.value })} style={{ padding: 6, marginRight: 6 }} />
        <input placeholder="label (e.g. Shipping)" value={newArea.label}
               onChange={(e) => setNewArea({ ...newArea, label: e.target.value })} style={{ padding: 6, marginRight: 6 }} />
        <button onClick={createArea}>Create subject area</button>
      </div>
    </div>
  );
}
